export type StatusTone =
  | 'success'
  | 'warn'
  | 'danger'
  | 'info'
  | 'neutral'
  | 'purple'
  | 'orange';

/** Canonical bg/fg colors per semantic tone — single source of truth for all status chips/badges. */
export const TONE_COLORS: Record<StatusTone, { bg: string; fg: string }> = {
  success: { bg: 'rgba(34,197,94,0.12)', fg: 'rgb(22,163,74)' },
  warn: { bg: 'rgba(245,158,11,0.12)', fg: 'rgb(161,98,7)' },
  danger: { bg: 'rgba(239,68,68,0.12)', fg: 'rgb(185,28,28)' },
  info: { bg: 'rgba(59,130,246,0.12)', fg: 'rgb(37,99,235)' },
  neutral: { bg: 'rgba(107,114,128,0.12)', fg: 'rgb(75,85,99)' },
  purple: { bg: 'rgba(168,85,247,0.12)', fg: 'rgb(126,34,206)' },
  orange: { bg: 'rgba(249,115,22,0.12)', fg: 'rgb(194,65,12)' }
};

const STATUS_TONE_MAP: Record<string, StatusTone> = {
  // Session / job lifecycle
  finished: 'success',
  completed: 'success',
  done: 'success',
  success: 'success',
  active: 'success',
  enabled: 'success',
  closed: 'success',
  approved: 'success',
  perpetual: 'success',

  // Customer types
  direct: 'info',
  reseller: 'purple',
  sub_org: 'success',

  processing: 'info',
  in_progress: 'info',
  inprogress: 'info',
  running: 'info',
  uploading: 'info',
  uploaded: 'info',
  trial: 'info',
  open: 'info',
  pay_as_you_go: 'info',
  translation: 'info',

  error: 'danger',
  failed: 'danger',
  failure: 'danger',
  suspended: 'danger',
  deleted: 'danger',
  dead_letter: 'danger',
  removed: 'danger',
  rejected: 'danger',

  pending: 'warn',
  queued: 'warn',
  waiting: 'warn',
  past_due: 'warn',
  pastdue: 'warn',
  bundle: 'warn',

  inactive: 'neutral',
  cancelled: 'neutral',
  canceled: 'neutral',
  expired: 'neutral',
  disabled: 'neutral',
  draft: 'neutral',
  new: 'neutral',
  other: 'neutral',

  // Document types
  transcript: 'purple',
  caption: 'orange',
  alignment: 'neutral',

  // Session output phases
  streaming: 'purple',
  transcribed: 'orange',
  transcoded: 'purple',
  invoiced: 'purple'
};

/** Map any raw status string from the backend to a semantic tone. Falls back to neutral. */
export function resolveStatusTone(status: string): StatusTone {
  const key = status.toLowerCase().replace(/[\s-]/g, '_');
  return STATUS_TONE_MAP[key] ?? 'neutral';
}

/** Convenience: get the bg/fg pair for any status string. */
export function getStatusColors(status: string): { bg: string; fg: string } {
  return TONE_COLORS[resolveStatusTone(status)];
}
